import React, { useState, useEffect } from 'react';
import { getSettings, submitContactForm } from '../../services/api';
import { Mail, Phone, Instagram, ExternalLink } from 'lucide-react';
import useReveal from '../../hooks/useReveal';
import '../../styles/contact.css';

const Contact = () => {
  const [photographerInfo, setPhotographerInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settings = await getSettings();
        setPhotographerInfo({
          email: settings.email,
          phone: settings.phone,
          social: {
            instagram: settings.social_instagram,
            behance: settings.social_behance,
            pinterest: settings.social_pinterest
          }
        });
      } catch (error) {
        console.error('Error loading contact data:', error);
      }
    };

    fetchData();
  }, []);

  useReveal([]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await submitContactForm(formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 3000);
    } catch (error) {
      setError('Failed to send message. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <div className="contact-header" data-reveal>
          <span className="contact-label">Get In Touch</span>
          <h2 className="contact-title">Let's Create Together</h2>
          <p className="contact-subtitle">
            Have a project in mind or want to discuss a collaboration? 
            I'd love to hear from you.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info" data-reveal style={{ '--reveal-delay': '150ms' }}>
            <div className="info-card">
              <div className="info-icon">
                <Mail size={24} />
              </div>
              <h3>Email</h3>
              <a href={`mailto:${photographerInfo?.email}`}>{photographerInfo?.email}</a>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <Phone size={24} />
              </div>
              <h3>Phone</h3>
              <a href={`tel:${photographerInfo?.phone}`}>{photographerInfo?.phone}</a>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <Instagram size={24} />
              </div>
              <h3>Social Media</h3>
              <div className="social-links">
                <a href={photographerInfo?.social?.instagram} target="_blank" rel="noopener noreferrer">
                  Instagram <ExternalLink size={14} />
                </a>
                <a href={photographerInfo?.social?.behance} target="_blank" rel="noopener noreferrer">
                  Behance <ExternalLink size={14} />
                </a>
                <a href={photographerInfo?.social?.pinterest} target="_blank" rel="noopener noreferrer">
                  Pinterest <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>

          <div className="contact-form-wrapper" data-reveal style={{ '--reveal-delay': '300ms' }}>
            {submitted ? (
              <div className="form-success">
                <div className="success-icon">✓</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. I'll get back to you soon.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                {error && <div className="form-error">{error}</div>}
                
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    data-testid="contact-subject-input"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Project Inquiry"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell me about your project..."
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn" data-testid="contact-submit-button" disabled={submitting} data-gas>
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;